/**
 * Custom PWA install affordance.
 *
 * - Auto-detects when the app is already installed/running standalone and shows nothing.
 * - On Android/Chromium, captures `beforeinstallprompt` and offers a one-tap install
 *   button that triggers the native prompt.
 * - On iOS Safari (no beforeinstallprompt) shows a short "Add to Home Screen" hint.
 *
 * The button is a lightweight DOM overlay (the game itself is a canvas), so it works
 * independently of Phaser scenes and survives scene restarts.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    // iOS Safari exposes navigator.standalone when launched from the home screen.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; detect the touch-capable Safari case too.
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function makeButton(label: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.textContent = label;
  Object.assign(btn.style, {
    position: 'fixed',
    right: '12px',
    bottom: '12px',
    zIndex: '20',
    padding: '10px 16px',
    borderRadius: '999px',
    border: '2px solid #59d2ff',
    background: 'rgba(11,16,32,0.86)',
    color: '#f4f7ff',
    font: '700 15px system-ui, -apple-system, sans-serif',
    boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
    cursor: 'pointer',
    display: 'none',
    touchAction: 'manipulation',
  } as CSSStyleDeclaration);
  document.body.appendChild(btn);
  return btn;
}

function showIosHint(): void {
  const hint = document.createElement('div');
  hint.innerHTML =
    'Per installare: tocca <b>Condividi</b> ⬆️ poi <b>Aggiungi a Home</b> ➕';
  Object.assign(hint.style, {
    position: 'fixed',
    left: '50%',
    bottom: '64px',
    transform: 'translateX(-50%)',
    zIndex: '21',
    maxWidth: '88vw',
    padding: '12px 16px',
    borderRadius: '14px',
    background: 'rgba(11,16,32,0.94)',
    border: '1px solid #59d2ff',
    color: '#f4f7ff',
    font: '500 14px system-ui, -apple-system, sans-serif',
    textAlign: 'center',
    boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
  } as CSSStyleDeclaration);
  document.body.appendChild(hint);
  window.setTimeout(() => hint.remove(), 6000);
}

/** Wire up install detection + prompt. Call once at startup. */
export function setupInstallPrompt(): void {
  // Already installed → never nag.
  if (isStandalone()) return;

  let deferred: BeforeInstallPromptEvent | null = null;
  const btn = makeButton('📲 Installa app');

  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop Chrome's mini-infobar; we drive the prompt from our own button.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    btn.style.display = 'block';
  });

  btn.addEventListener('click', () => {
    if (!deferred) return;
    void deferred.prompt();
    void deferred.userChoice.finally(() => {
      deferred = null;
      btn.style.display = 'none';
    });
  });

  // Once installed, drop the button (and remember, so a non-standalone web tab that was
  // installed elsewhere doesn't keep showing it this session).
  window.addEventListener('appinstalled', () => {
    deferred = null;
    btn.remove();
  });

  // iOS Safari never fires beforeinstallprompt: offer a manual hint button instead.
  if (isIos()) {
    btn.style.display = 'block';
    btn.addEventListener('click', () => {
      if (!deferred) showIosHint();
    });
  }
}
